import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, LogOut, Trash2, UserCircle } from 'lucide-react';
import {
  ApiError,
  changePassword,
  deleteAccount,
  qk,
  revokeOtherSessions,
  updateProfile,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Card from '../components/Card.js';
import { useAuth } from '../components/AuthGate.js';
import { LOGIN_PATH, passwordProblem } from '../lib/auth.js';

/**
 * §73.1 — Hesap sayfası: kimlik · parola · oturumlar.
 *
 * NEDEN VAR: M38'e kadar panelde kullanıcının KENDİ hesabına dair tek bir ekran
 * yoktu. `changePassword` istemcisi (api.ts) ve `POST /api/auth/password` ucu ikisi
 * de YAZILMIŞTI ve hiçbir yerden çağrılmıyordu — yani parola değiştirme özelliği
 * üründe VARDI ama kullanıcı ona ulaşamıyordu. Bu sayfa yeni bir yetenek eklemez,
 * var olanı ulaşılabilir kılar.
 *
 * E-POSTA SALT-OKUNURDUR (§71.2 KARAR 5): adres değiştirme çift doğrulama ve
 * benzersizlik yarışları getirir, ayrı bir karardır. Alanı gizlemek yerine devre
 * dışı gösterip SEBEBİNİ yazıyoruz — eksikliği saklamak, kullanıcıyı aramaya iter.
 */

/** §27.4 hata sözleşmesi DURUM KODUNDAN okunur (mesaj metninden değil). */
function hataMetni(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError) {
    if (error.code === 'invalid_credentials') return 'Mevcut parolanız hatalı.';
    if (error.code === 'service_token') {
      return 'Servis belirteciyle (ADMIN_TOKEN) gezerken hesap işlemleri yapılamaz.';
    }
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

/* -------------------------------------------------------------------------- */

function KimlikKarti() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [ad, setAd] = useState('');
  const [kaydedildi, setKaydedildi] = useState(false);

  const kaydet = useMutation({
    mutationFn: () => updateProfile(ad.trim()),
    onSuccess: async () => {
      setAd('');
      setKaydedildi(true);
      // Avatar baş harfleri ve menü başlığı `me`den okur — tazelenmeli.
      await queryClient.invalidateQueries({ queryKey: qk.me() });
    },
  });

  const hata = hataMetni(kaydet.error);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <UserCircle className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
          Kimlik
        </span>
      }
      subtitle="Ad soyad panelde ve gönderdiğiniz davetlerde görünür."
    >
      <form
        className="flex flex-wrap items-end gap-3"
        data-testid="hesap-kimlik-formu"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (ad.trim() !== '' && !kaydet.isPending) kaydet.mutate();
        }}
      >
        <label className="flex min-w-[200px] flex-1 flex-col gap-1">
          <span className="text-[12px] text-[#6b7280]">Ad soyad</span>
          <input
            data-testid="hesap-ad"
            type="text"
            value={ad}
            placeholder={user?.fullName ?? 'Adınız Soyadınız'}
            maxLength={120}
            onChange={(event) => {
              setAd(event.target.value);
              setKaydedildi(false);
            }}
            className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] outline-none placeholder:text-[#9ca3af] focus:border-[#d1d5db]"
          />
        </label>

        <label className="flex min-w-[200px] flex-1 flex-col gap-1">
          <span className="text-[12px] text-[#6b7280]">E-posta</span>
          <input
            data-testid="hesap-eposta"
            type="email"
            value={user?.email ?? ''}
            disabled
            readOnly
            className="h-8 cursor-not-allowed rounded-lg border border-[#e5e7eb] bg-[#f9fafb] px-3 text-[13px] text-[#6b7280] outline-none"
          />
        </label>

        <button
          type="submit"
          data-testid="hesap-ad-kaydet"
          className="btn-primary"
          disabled={ad.trim() === '' || kaydet.isPending}
        >
          {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      </form>

      <p className="mt-2 text-[12px] text-[#9ca3af]">
        E-posta adresi değiştirilemez: adres hem kimliğiniz hem de davetlerin
        eşleştiği anahtardır. Değiştirmeniz gerekiyorsa org yöneticinizle görüşün.
      </p>

      {kaydedildi ? (
        <p data-testid="hesap-ad-ok" className="mt-2 text-[12px] text-emerald-600">
          Adınız güncellendi.
        </p>
      ) : null}
      {hata !== null ? (
        <p data-testid="hesap-kimlik-hata" role="alert" className="mt-2 text-[12px] text-red-600">
          {hata}
        </p>
      ) : null}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */

function ParolaKarti() {
  const [mevcut, setMevcut] = useState('');
  const [yeni, setYeni] = useState('');
  const [bitti, setBitti] = useState(false);

  const degistir = useMutation({
    mutationFn: () => changePassword(mevcut, yeni),
    onSuccess: () => {
      setMevcut('');
      setYeni('');
      setBitti(true);
    },
  });

  /*
   * Politika İSTEMCİDE de ölçülür ama SUNUCUNUN YERİNE GEÇMEZ (`checkPasswordPolicy`
   * orada da koşar): amaç, kullanıcıyı kesin reddedilecek bir parolayı yazıp
   * göndermekten kurtarmaktır. Kural TEK yerde tanımlıdır (`lib/auth.ts`).
   */
  const politika = yeni === '' ? null : passwordProblem(yeni);
  const hata = hataMetni(degistir.error);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
          Parola
        </span>
      }
      subtitle="Parolanızı değiştirdiğinizde DİĞER cihazlardaki oturumlarınız kapanır; bu cihaz açık kalır."
    >
      <form
        className="flex flex-wrap items-end gap-3"
        data-testid="hesap-parola-formu"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (mevcut !== '' && yeni !== '' && politika === null && !degistir.isPending) {
            degistir.mutate();
          }
        }}
      >
        <label className="flex min-w-[200px] flex-1 flex-col gap-1">
          <span className="text-[12px] text-[#6b7280]">Mevcut parola</span>
          <input
            data-testid="hesap-parola-mevcut"
            type="password"
            autoComplete="current-password"
            value={mevcut}
            onChange={(event) => {
              setMevcut(event.target.value);
              setBitti(false);
            }}
            className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] outline-none focus:border-[#d1d5db]"
          />
        </label>

        <label className="flex min-w-[200px] flex-1 flex-col gap-1">
          <span className="text-[12px] text-[#6b7280]">Yeni parola</span>
          <input
            data-testid="hesap-parola-yeni"
            type="password"
            autoComplete="new-password"
            value={yeni}
            onChange={(event) => {
              setYeni(event.target.value);
              setBitti(false);
            }}
            className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] outline-none focus:border-[#d1d5db]"
          />
        </label>

        <button
          type="submit"
          data-testid="hesap-parola-kaydet"
          className="btn-primary"
          disabled={mevcut === '' || yeni === '' || politika !== null || degistir.isPending}
        >
          {degistir.isPending ? 'Değiştiriliyor…' : 'Parolayı değiştir'}
        </button>
      </form>

      {politika !== null ? (
        <p data-testid="hesap-parola-politika" className="mt-2 text-[12px] text-[#b45309]">
          {politika}
        </p>
      ) : null}
      {bitti ? (
        <p data-testid="hesap-parola-ok" className="mt-2 text-[12px] text-emerald-600">
          Parolanız değişti; diğer cihazlardaki oturumlar kapatıldı.
        </p>
      ) : null}
      {hata !== null ? (
        <p data-testid="hesap-parola-hata" role="alert" className="mt-2 text-[12px] text-red-600">
          {hata}
        </p>
      ) : null}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */

function OturumlarKarti() {
  const [onay, setOnay] = useState(false);

  const kapat = useMutation({
    mutationFn: () => revokeOtherSessions(),
    onSuccess: () => setOnay(false),
  });

  const hata = hataMetni(kapat.error);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <LogOut className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
          Oturumlar
        </span>
      }
      subtitle="Başka bir cihazda açık kalmış oturumdan şüpheleniyorsanız hepsini tek seferde kapatın."
    >
      {/*
        OTURUM LİSTESİ YOKTUR ve bu bilinçlidir: cihaz/tarayıcı bilgisi tutan bir
        tablo yok. "Chrome · İstanbul · 2 saat önce" gibi bir liste UYDURMAK, kullanıcının
        güvenlik kararını sahte veriye dayandırması demekti. Tek dürüst fiil sunulur.
      */}
      {kapat.isSuccess ? (
        <p data-testid="hesap-oturum-ok" className="text-[13px] text-emerald-600">
          {kapat.data.closed === 0
            ? 'Kapatılacak başka oturum yoktu.'
            : `${kapat.data.closed} oturum kapatıldı.`}
        </p>
      ) : onay ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] text-[#374151]">
            Bu cihaz DIŞINDAKİ tüm oturumlar kapanacak. Emin misiniz?
          </span>
          <button
            type="button"
            data-testid="hesap-oturum-onayla"
            className="btn-primary"
            disabled={kapat.isPending}
            onClick={() => kapat.mutate()}
          >
            {kapat.isPending ? 'Kapatılıyor…' : 'Evet, kapat'}
          </button>
          <button type="button" className="btn-ghost" onClick={() => setOnay(false)}>
            Vazgeç
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="hesap-oturum-kapat"
          className="btn-ghost"
          onClick={() => setOnay(true)}
        >
          Diğer cihazlardaki oturumları kapat
        </button>
      )}

      {hata !== null ? (
        <p data-testid="hesap-oturum-hata" role="alert" className="mt-2 text-[12px] text-red-600">
          {hata}
        </p>
      ) : null}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * §85.2(a) — HESAP SİLME (tehlikeli bölge).
 *
 * Proje arşivlemenin görsel diliyle aynı (§72.4). Farkı: proje SİLİNMEZ ama hesap
 * SİLİNİR — kişi kendi verisi üzerinde hak sahibidir, proje ise müşterinin iş
 * varlığıdır ve geçmişi başkalarını da ilgilendirir.
 */
function HesapSilKarti() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [onay, setOnay] = useState(false);
  const [parola, setParola] = useState('');

  const sil = useMutation({
    mutationFn: () => deleteAccount(parola),
    onSuccess: () => {
      queryClient.clear();
      navigate(LOGIN_PATH, { replace: true });
    },
  });

  const hata =
    sil.error instanceof ApiError && sil.error.code === 'last_owner'
      ? 'Son sahibi olduğunuz bir organizasyon ya da proje var. Önce başka bir üyeyi sahip yapın; yönetilemez bir org bırakmak veri silmekten daha kalıcı bir hasardır.'
      : sil.error instanceof ApiError && sil.error.code === 'invalid_credentials'
        ? 'Parolanız hatalı.'
        : hataMetni(sil.error);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
          Hesabı sil
        </span>
      }
      subtitle="Hesabınız ve tüm üyelikleriniz kalıcı olarak silinir. Bu işlem geri alınamaz."
    >
      <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-4 py-3">
        <p className="text-[12px] text-[#b91c1c]">
          Silinen: hesabınız, proje ve organizasyon üyelikleriniz, bekleyen davetleriniz.
          {' '}
          <strong>Silinmeyen:</strong> projelerin analitik verisi (o veri size değil
          müşteriye/organizasyona aittir) ve e-posta gönderim geçmişi — adresiniz o
          kayıtlarda <strong>maskelenir</strong>, çünkü gönderim geçmişi bir denetim
          kaydıdır.
        </p>

        {onay ? (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            {/*
              §85.2(a) — YIKICI İŞLEMDE KİMLİK TAZELENİR. SSO ile gelen kullanıcının
              parolası olmayabilir; sunucu o durumda parola SORMAZ ve alan boş
              bırakılabilir (aksi hâlde SSO kullanıcısı hesabını hiç silemezdi).
            */}
            <label className="flex min-w-[200px] flex-col gap-1">
              <span className="text-[12px] text-[#991b1b]">
                Onaylamak için parolanız (SSO ile girdiyseniz boş bırakın)
              </span>
              <input
                data-testid="hesap-sil-parola"
                type="password"
                autoComplete="current-password"
                value={parola}
                onChange={(event) => setParola(event.target.value)}
                className="h-8 rounded-lg border border-[#fecaca] bg-white px-3 text-[13px] text-[#111827] outline-none"
              />
            </label>
            <button
              type="button"
              data-testid="hesap-sil-onayla"
              className="btn-primary"
              disabled={sil.isPending}
              onClick={() => sil.mutate()}
            >
              {sil.isPending ? 'Siliniyor…' : 'Evet, hesabımı sil'}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setOnay(false)}>
              Vazgeç
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="hesap-sil"
            className="btn-ghost mt-3"
            onClick={() => setOnay(true)}
          >
            Hesabımı sil
          </button>
        )}

        {hata !== null ? (
          <p data-testid="hesap-sil-hata" role="alert" className="mt-2 text-[12px] text-red-600">
            {hata}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */

export default function Hesap() {
  const { serviceToken } = useAuth();

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="hesap-sayfasi">
      <div className="border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <UserCircle size={18} className="text-[#6b7280]" aria-hidden="true" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-[18px] font-semibold text-[#111827]">Hesap</h1>
              <BilgiDugmesi testId="hesap-bilgi">Kendi hesabınızı yönetirsiniz: ad, parola değişikliği ve açık oturumlarınız — tanımadığınız bir cihazın oturumunu buradan kapatabilirsiniz. Hesabı kalıcı silme de buradadır; proje ayarları "Ayarlar", organizasyon "Organizasyon" sayfasındadır.</BilgiDugmesi>
            </div>
            <p className="text-[13px] text-[#6b7280]">
              Kimliğiniz, parolanız ve açık oturumlarınız — proje ayarları için “Ayarlar”,
              organizasyon için “Organizasyon” sayfasına bakın.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[860px] space-y-6 px-6 py-8">
        {serviceToken ? (
          /*
           * §27.6 — servis belirtecinin `users` kaydı YOKTUR: üç kartın üçü de 403
           * dönerdi. Formları çizip hepsini hataya sürmek yerine durum söylenir.
           */
          <p data-testid="hesap-servis-belirteci" className="text-[13px] text-[#6b7280]">
            Panele servis belirteciyle (ADMIN_TOKEN) bakıyorsunuz. Bu bir kullanıcı
            hesabı değildir; kimlik, parola ve oturum işlemleri yalnız gerçek hesaplar
            içindir.
          </p>
        ) : (
          <>
            <KimlikKarti />
            <ParolaKarti />
            <OturumlarKarti />
            {/* §85.2(a) — EN ALTTA: geri alınamaz işlem, gündelik akışın dışında. */}
            <HesapSilKarti />
          </>
        )}
      </div>
    </div>
  );
}
